import { type MouseEvent, type PointerEvent as ReactPointerEvent, useCallback } from 'react'
import { isDescendantOfGroup } from '@/lib/groups/shape-group-tree'
import { isSelectionStateBadgeTarget } from '@/components/app/editor/selection-chrome/selection-chrome-constants'
import type { ShapeManagerStateApi } from './use-shape-manager-state'
import type { SelectionHistoryApi } from './use-selection-history'
import type { EditScopeRefsApi } from './use-edit-scope-refs'
import type { SelectionPresentationApi } from './use-selection-presentation'
import type { ShapeSelectionApi } from './use-shape-selection'
import type { GestureStartApi } from './use-gesture-start'
import type { ShapeHitTestingApi } from './use-shape-hit-testing'
import type { AltClickCycleApi } from './use-alt-click-cycle'

export function useSelectionBoxPointerHandlers(
  ctx: ShapeManagerStateApi &
    SelectionHistoryApi &
    EditScopeRefsApi &
    SelectionPresentationApi &
    ShapeSelectionApi &
    GestureStartApi &
    ShapeHitTestingApi &
    AltClickCycleApi
) {
  const {
    altHoveredShapeId,
    ctrlCycleRef,
    getCtrlCycleTargetAtPoint,
    getLocalPointFromEvent,
    getStackAtPoint,
    handleAltClick,
    handleMoveStart,
    handleShiftAltClick,
    isolationRootGroupIdRef,
    isSelectionEnabled,
    onExitGroupIsolationModeRef,
    onShapeContextMenu,
    recordShapeSelectionHistory,
    resetAltClickCycle,
    selectedShape,
    selectedShapeIds,
    selectedShapeIdsRef,
    setAltHoveredShapeId,
    setSelectedShapeIds,
    shapesRef,
    toggleSelection,
  } = ctx

  // Marquee selection itself lives entirely in CenterCanvas's unified (shape + text) pointer
  // handling (`center-canvas/hooks/use-marquee-select.ts`) — do not reintroduce it here. This
  // handler's one non-duplicate job is exiting Group Isolation Mode when the click hit-tests (via
  // getStackAtPoint, independent of pointer-events CSS) to a shape stack outside the isolation
  // root's subtree — CenterCanvas can't do this check itself since it has no access to the shape
  // hit-stack at a point.
  const handleCanvasPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      if (!isSelectionEnabled || event.button !== 0) return
      if (event.target !== event.currentTarget) return

      ctrlCycleRef.current = null

      const isolationRootId = isolationRootGroupIdRef.current

      if (!isolationRootId) return

      const start = getLocalPointFromEvent(event)

      if (!start) return

      const shapesAtPoint = getStackAtPoint(start)
      const byId = new Map(shapesRef.current.map((shape) => [shape.id, shape]))
      const clickedInsideIsolationRoot = shapesAtPoint.some(
        (shape) => shape.id === isolationRootId || isDescendantOfGroup(shape, isolationRootId, byId)
      )

      if (!clickedInsideIsolationRoot) {
        event.preventDefault()
        onExitGroupIsolationModeRef.current?.()
      }
    },
    [
      getLocalPointFromEvent,
      getStackAtPoint,
      isSelectionEnabled,
      ctrlCycleRef,
      isolationRootGroupIdRef,
      onExitGroupIsolationModeRef,
      shapesRef,
    ]
  )

  const handleSelectionBoxPointerDown = useCallback(
    (event: ReactPointerEvent) => {
      if (!isSelectionEnabled || event.button !== 0) return
      if (isSelectionStateBadgeTarget(event.target)) return
      if (!event.altKey) resetAltClickCycle()

      if (event.shiftKey && event.altKey) {
        handleShiftAltClick(event)
        return
      }

      if (event.ctrlKey || event.metaKey || event.shiftKey) {
        const point = getLocalPointFromEvent(event)

        event.preventDefault()
        event.stopPropagation()
        const target = point ? getCtrlCycleTargetAtPoint(point) : selectedShape

        if (target) {
          toggleSelection(target.id)
        }

        return
      }

      if (event.altKey) {
        handleAltClick(event)
        return
      }

      ctrlCycleRef.current = null
      handleMoveStart(event)
    },
    [
      getCtrlCycleTargetAtPoint,
      getLocalPointFromEvent,
      handleAltClick,
      handleMoveStart,
      handleShiftAltClick,
      isSelectionEnabled,
      resetAltClickCycle,
      selectedShape,
      toggleSelection,
      ctrlCycleRef,
    ]
  )

  const handleMultiSelectChromeBoxPointerDown = useCallback(
    (event: ReactPointerEvent) => {
      if (!isSelectionEnabled || event.button !== 0) return
      if (isSelectionStateBadgeTarget(event.target)) return
      if (!event.altKey) resetAltClickCycle()
      const point = getLocalPointFromEvent(event)

      if (event.shiftKey && event.altKey) {
        handleShiftAltClick(event)
        return
      }

      if ((event.ctrlKey || event.metaKey || event.shiftKey) && point) {
        event.preventDefault()
        event.stopPropagation()
        const target = getCtrlCycleTargetAtPoint(point)

        if (target) {
          // If deselecting the alt-hovered shape, clear the highlight
          if (altHoveredShapeId === target.id && selectedShapeIds.includes(target.id)) {
            setAltHoveredShapeId(null)
          }

          toggleSelection(target.id)
        }

        return
      }

      if (event.altKey) {
        handleAltClick(event)
        return
      }

      const shapesAtPoint = point ? getStackAtPoint(point) : []

      // Clear alt-hovered state on regular click
      setAltHoveredShapeId(null)
      if (shapesAtPoint.length === 0) {
        // Clicked inside multi-select bounds but not on any shape - clear selection
        setSelectedShapeIds((prev) => {
          const next: string[] = []

          selectedShapeIdsRef.current = next
          recordShapeSelectionHistory(prev, next)
          return next
        })
        return
      }

      // Pass the topmost clicked shape for click-to-select-single behavior
      const clickedShape = shapesAtPoint[0]

      ctrlCycleRef.current = null
      handleMoveStart(event, clickedShape?.id)
    },
    [
      altHoveredShapeId,
      getCtrlCycleTargetAtPoint,
      getLocalPointFromEvent,
      getStackAtPoint,
      handleAltClick,
      handleMoveStart,
      handleShiftAltClick,
      isSelectionEnabled,
      selectedShapeIds,
      toggleSelection,
      recordShapeSelectionHistory,
      resetAltClickCycle,
      ctrlCycleRef,
      selectedShapeIdsRef,
      setAltHoveredShapeId,
      setSelectedShapeIds,
    ]
  )

  const handleSelectionBoxContextMenu = useCallback(
    (event: MouseEvent) => {
      event.preventDefault()
      event.stopPropagation()

      const selectedIds = selectedShapeIdsRef.current

      if (selectedIds.length === 0) return

      onShapeContextMenu?.(selectedIds, event)
    },
    [onShapeContextMenu, selectedShapeIdsRef]
  )

  return {
    handleCanvasPointerDown,
    handleSelectionBoxPointerDown,
    handleMultiSelectChromeBoxPointerDown,
    handleSelectionBoxContextMenu,
  }
}

export type SelectionBoxPointerHandlersApi = ReturnType<typeof useSelectionBoxPointerHandlers>
